import { ApplicationElement, html } from '../application_element/element.js';
import { actionSheetController } from '@ionic/core';
import { defineCustomElement as defineIonActionSheet } from '@ionic/core/components/ion-action-sheet.js';
import { initialize } from '@ionic/core/components/index.js';
import { classMap } from 'lit/directives/class-map.js';
import { compareVersions } from '../../lib/helpers.js';
import translations from './translations.js';
import '../dropdown/element.js';
import '../icon/element.js';
import '../icon_block/element.js';
import style from './style.lit.css';

initialize();
defineIonActionSheet();

/**
 * Dot menu renders different type of menu depending on platform and screen width.
 *
 * **iOS/Android applications:**
 * Sends config for rendering native menu via post message method.
 *
 * **Browsers with screens smaller than 768px:**
 * Sends config for rendering Ionic menu which simulates native behavior. It is different style for iOS and Android apps.
 *
 * **Browsers with screens larger than 768px:**
 * Renders normal dropdown menu
 *
 * ## Config
 * #### Options
 * |Property   |Type       |Optional|
 * |-----------|-----------|
 * |header     |string     |        |
 * |subheader  |string     |true    |
 * |showCancel |boolean    |true    |
 * |buttons    |array      |        |
 *
 * #### Buttons
 * |Property   |Type       |Optional|
 * |-----------|-----------|--------|
 * |icon       |string     |true    |
 * |title      |string     |        |
 * |action     |object     |        |
 * |role       |string     |true    |
 * |partName   |string     |true    |
 * |isDivider  |boolean    |true    |
 *
 * #### Action
 * |Property   |Type   |Optional|
 * |-----------|-------|
 * |menu       |this   |        |
 * |id         |number |        |
 * |method     |string |        |
 * |args       |object |true    |
 *
 * *If isDivider is set for button it will generate simple divider element only for normal dropdown menu in browsers with screens larger than 768px.
 *
 * *If menu is shown by an embedded page, it will always show as a dropdown.
 *
 */
export class DotMenu extends ApplicationElement {
  static styles = [ApplicationElement.styles, style];

  /** @internal */
  static translations = translations;

  static properties = {
    config: { type: Object },
    boundaryElement: { type: Object },
    buttonClass: { type: String },
  };

  static defaults = {
    deprecatedAppVersion: null,
    mobileAppVersion: null,
  };

  /** @internal */
  handleResize = () => {
    this.requestUpdate();
  };

  constructor() {
    super();
    window.addEventListener('resize', this.handleResize);
  }

  /** @internal */
  get buttonsWithoutDividers() {
    // In native menu there is no dividers so we are removing them from config
    return this.config.buttons.filter(item => !item.isDivider);
  }

  /** @internal */
  get ionicConfig() {
    // {
    //   header: 'Some title',
    //   subHeader: 'Some message',
    //   buttons: [
    //     {
    //       text: 'Visit',
    //       handler: this.visit({url: "www.example.com"}),
    //       customIcon: 'icon-name',
    //       partName: 'my-custom-part-name',
    //     },
    //     {
    //       text: 'Edit',
    //       handler: this.execJSFromResponse({url: "www.example.com"}),
    //     },
    //     {
    //       text: 'Delete',
    //       handler: this.delete(),
    //       role: 'destructive',
    //     },
    //     {
    //       text: 'Cancel',
    //       customIcon: 'close',
    //       role: 'cancel',
    //     },
    //   ],
    // }

    const cancelButton = {
      text: this.t('actions.cancel'),
      customIcon: 'close',
      role: 'cancel',
    };

    const buttons = this.buttonsWithoutDividers.map(item => {
      const { role, partName, icon } = item;

      return {
        text: item.title,
        handler: () => this.generateBrowserAction(item.action),
        ...(role && { role }),
        ...(partName && { cssClass: partName }),
        ...(icon && { customIcon: icon }),
      };
    });

    this.config.showCancel && buttons.push(cancelButton);

    return {
      header: this.config.header,
      ...(this.config.subheader && { subheader: this.config.subheader }),
      buttons: buttons,
    };
  }

  /** @internal */
  get nativeConfig() {
    //  {
    //   title: "Some title",
    //   message: "Some message",
    //   showCancel: true,
    //   items: [
    //    {
    //      action: "actionManager.CardMenu.index[1].visit({url: "www.example.com"})",
    //      title: "Visit",
    //      icon: "logo",
    //    },
    //    {
    //      action: "actionManager.CardMenu.index[1].execJSFromResponse({url: "www.example.com"})",
    //      title: "Edit",
    //    },
    //    {
    //      action: "actionManager.CardMenu.index[1].delete()",
    //      title: "Delete",
    //      destructive: true,
    //    }
    //  ]
    // }

    const { header, subheader, showCancel } = this.config;

    return {
      title: header,
      ...(subheader && { message: subheader }),
      ...(showCancel && { showCancel: true }),
      items: this.buttonsWithoutDividers.map(item => {
        return {
          action: this.generateMobileNativeAction(item.action),
          ...(item.role === 'destructive' && { destructive: true }),
          icon: item.icon,
          title: item.title,
        };
      }),
    };
  }

  /** @internal */
  get isMobileApp() {
    return !!this.constructor.defaults.mobileAppVersion;
  }

  /** @internal */
  get isSmallDevice() {
    return window.innerWidth < 768;
  }

  /** @internal */
  get shouldRenderNativeMenu() {
    return (
      this.isMobileApp &&
      compareVersions(
        this.constructor.defaults.mobileAppVersion,
        this.constructor.defaults.deprecatedAppVersion
      ) == 1
    );
  }

  /** @internal */
  get isInIframe() {
    return window.self !== window.top;
  }

  /** @internal */
  get shouldRenderIonicMenu() {
    return (
      !this.shouldRenderNativeMenu && this.isSmallDevice && !this.isInIframe
    );
  }

  /** @internal */
  get shouldRenderDropdownMenu() {
    return !this.shouldRenderIonicMenu && !this.shouldRenderNativeMenu;
  }

  /** @internal */
  get dropdown() {
    return this.shadowRoot.querySelector('ef-dropdown');
  }

  generateBrowserAction(action) {
    const { menu, id, method, args } = action;
    const instance = window.commonActions[menu].index[id];

    return instance[method].apply(instance, args);
  }

  generateMobileNativeAction(action) {
    const { menu, id, method, args } = action;

    const instance = `commonActions.${menu}.index[${id}]`;
    const stringifyArgs = args ? JSON.stringify(args) : '';

    return `${instance}.${method}(${stringifyArgs})`;
  }

  handleNativeMenu() {
    window.App?.Mobile?.postMessage('openActionSheet', this.nativeConfig);
  }

  async handleIonicMenu() {
    const { ionicConfig } = this;

    this.actionSheet = await actionSheetController.create({
      header: ionicConfig.header,
      subHeader: ionicConfig.subHeader,
      buttons: ionicConfig.buttons,
    });

    this.actionSheet.addEventListener('ionActionSheetWillPresent', e => {
      const items = e.target.querySelectorAll('.action-sheet-group button');

      // Add icons
      items.forEach(item => {
        const result = ionicConfig.buttons.find(
          obj => obj.text === item.innerText
        );
        const newIcon = this.createIonicIconElement();

        newIcon.name = result.customIcon;
        item.firstChild.prepend(newIcon);
      });
    });

    this.actionSheet.present();
  }

  open() {
    if (this.shouldRenderDropdownMenu) {
      this.dropdown.open = true;
    } else {
      return this.openIonicOrNativeMenu();
    }
  }

  openIonicOrNativeMenu() {
    return this.shouldRenderIonicMenu
      ? this.handleIonicMenu()
      : this.handleNativeMenu();
  }

  createIonicIconElement() {
    return document.createElement('ef-icon');
  }

  renderButton(item) {
    return html`
      <button
        @click=${() => this.generateBrowserAction(item.action)}
        class="item-with-icon">
        <ef-icon-block
          text=${item.title}
          icon=${item.icon}
          part=${item.partName ? `icon-block ${item.partName}` : 'icon-block'}>
        </ef-icon-block>
      </button>
    `;
  }

  renderMenuItems() {
    return html`${this.config?.buttons.map(item => {
      return html`
        <li class=${classMap({ divider: item.isDivider })}>
          ${item.isDivider ? null : this.renderButton(item)}
        </li>
      `;
    })}`;
  }

  renderTrigger() {
    return html`
      <button class=${this.buttonClass || 'trigger'} aria-label="toggle button">
        <ef-icon name="ellipsis-h"></ef-icon>
      </button>
    `;
  }

  renderDropdownMenu() {
    return html`
      <ef-dropdown
        caret
        offsetVertical="16"
        .boundaryElement=${this.boundaryElement}>
        <slot name="trigger" slot="trigger">${this.renderTrigger()}</slot>
        <ul>
          ${this.renderMenuItems()}
        </ul>
      </ef-dropdown>
    `;
  }

  render() {
    if (this.shouldRenderDropdownMenu) {
      return this.renderDropdownMenu();
    } else {
      return html`
        <slot name="trigger" @click=${this.openIonicOrNativeMenu}>
          ${this.renderTrigger()}
        </slot>
      `;
    }
  }
}

DotMenu.registerTagName('ef-dot-menu');

customElements.get('ef-dot-menu') ||
  customElements.define('ef-dot-menu', DotMenu);

export default DotMenu;
